import isNumber from 'lodash-es/isNumber'
import sumBy from 'lodash-es/sumBy'
import { TransactionBuilder, Transaction as RawTransaction, ECPairInterface as ECPair, Network, script, opcodes, address as addressHelper } from 'bitcoinjs-lib'

import { ILogger, IBTCUnspent, IBTCInput, IBTCOutput, BTCAddress, OneChainError } from '@one-chain/core'
import { IApiProvider, IFastApiProvider, ISlowApiProvider } from './api-providers'
import { isValidAddress, fromSat } from './helper'

/**
 * OmniTransaction
 *
 * @export
 * @class OmniTransaction
 */
export class OmniTransaction {
  protected _apiProvider: IApiProvider
  protected _logger: ILogger
  protected _network: Network
  protected _wallets?: { [address: string]: ECPair } = null

  protected _transaction: RawTransaction // 交易对象
  protected _unspents: IBTCUnspent[] // 转出地址总 unspents
  protected _inputs: IBTCInput[] // 所有的输入
  protected _outputs: IBTCOutput[] // 所有的输出
  protected _value: number // 转出地址总余额
  protected _propertyId: number // 代币类型 ID，31 就是 USDT
  protected _type: number // Omni 交易类型
  protected _typeName: string // Omni 交易类型名称
  protected _change: number // 找零总额
  protected _fee: number = 0 // 矿工费

  protected _from: BTCAddress
  protected _to: BTCAddress

  // 最小转账金额
  protected _minimalValue = 546

  get value () {
    return this._value
  }

  get change () {
    return this._change
  }

  get fee () {
    return this._fee
  }

  get unspents () {
    return this._unspents
  }

  constructor (from: BTCAddress, to: BTCAddress, value: number, propertyId: number) {
    this._validateAddress(from)
    this._validateAddress(to)
    this._validateValue(value)

    this._from = from
    this._to = to
    this._value = value
    this._propertyId = propertyId
  }

  async init (
    apiProvider: IApiProvider,
    logger: ILogger,
    network: Network,
    wallets: { [address: string]: ECPair } = null,
  ) {
    this._apiProvider = apiProvider
    this._logger = logger
    this._network = network
    this._wallets = wallets

    await this._loadUnspents(this._from)
    this._transaction = this._buildTransaction(this._from, this._to, this._value, this._propertyId, this._unspents)
  }

  /**
   * 编辑交易内容
   *
   * @param {number} [fee=-1]
   * @param {BTCAddress} [to='']
   * @memberof OmniTransaction
   */
  edit (fee: number = -1, to: BTCAddress = '', value: number = -1, propertyId: number = -1) {
    if (isNumber(fee) && fee > 0) {
      this._fee = fee
    }
    if (to) {
      this._validateAddress(to)
      this._to = to
    }
    if (value !== -1) {
      this._validateValue(value)
      this._value = value
    }
    if (propertyId !== -1) {
      this._propertyId = propertyId
    }

    this._transaction = this._buildTransaction(this._from, this._to, this._value, this._propertyId, this._unspents, fee)
  }

  /**
   * 试算交易的矿工费
   *
   * @param {number} feeRate
   * @returns
   * @memberof OmniTransaction
   */
  calcFee (feeRate: number) {
    this._logger.debug('BTC - Transaction.calcFee calculate transaction fee.')
    this._validateFeeRate(feeRate)

    // 计算矿工费
    const virtualSizeBefore = this._transaction.virtualSize()
    let virtualSize = virtualSizeBefore
    let fee = feeRate * virtualSize

    // 校验新的 feeRate 下交易的体积
    // 关于为什么需要校验：https://bitcoin.stackexchange.com/questions/1195/how-to-calculate-transaction-size-before-sending-legacy-non-segwit-p2pkh-p2sh
    const transaction = this._buildTransaction(this._from, this._to, this._value, this._propertyId, this._unspents, fee)
    const virtualSizeAfter = transaction.virtualSize()
    // 新的 feeRate 下交易的体积发生了变化，那么就以变化后的体积为准，通常这会导致多付一点手续费，但是比少付更能确保交易的及时性
    if (virtualSizeBefore !== virtualSizeAfter) {
      this._logger.debug('BTC - Transaction.calcFee recalculate transaction fee.')
      virtualSize = virtualSizeAfter
      fee = feeRate * virtualSize
    }

    this._logger.debug(`BTC - Transaction.calcFee totalFee[${fee}] = feeRate[${feeRate}] * virtualSize[${virtualSize}]`)
    return fee
  }

  /**
   * 生成 hex 编码交易数据
   *
   * @returns
   * @memberof Transaction
   */
  toHex () {
    return this._transaction.toHex()
  }

  /**
   * 生成 JSON 格式的交易数据
   *
   * @returns
   * @memberof OmniTransaction
   */
  toJSON () {
    // TODO 转为通用数据结构，将自定义结构分装到其他库总
    return {
      type: this._typeName,
      detail: {
        confirmations: -1,
        receivetime: '1970-01-01T00:00:00+0000',
        blocktime: '1970-01-01T00:00:00+0000',
        info: {
          txid: this._transaction.getId(),
          fee: fromSat(this._fee).toFixed(),
          sendingaddress: this._from,
          referenceaddress: this._to,
          ismine: false,
          version: 0,
          type_int: this._type,
          type: this._typeName,
          valid: false,
          invalidreason: '',
          blockhash: '',
          blocktime: -1,
          positioninblock: -1,
          block: -1,
          propertyid: this._propertyId,
          divisible: true,
          amount: fromSat(this._value).toFixed(),
        },
      },
    }
  }

  /**
   * 载入 unspents
   *
   * @protected
   * @param {BTCAddress} from
   * @memberof OmniTransaction
   */
  protected async _loadUnspents (from: BTCAddress) {
    this._logger.trace('BTC - OmniTransaction._loadUnspents from address: ', from)

    // 查询支付地址的 unspent
    let unspents
    if (this._apiProvider.supportBatch) {
      unspents = await (<IFastApiProvider> this._apiProvider).getUnspentOfAddresses([from])
    }
    else {
      unspents = await (<ISlowApiProvider> this._apiProvider).getUnspentOfAddress(from)
    }

    this._unspents = unspents
  }

  /**
   * 构造 bitcoinjs 交易对象
   *
   * @protected
   * @param {BTCAddress} from
   * @param {BTCAddress} to
   * @param {number} value
   * @param {IBTCUnspent[]} unspents
   * @param {number} [fee=0]
   * @returns
   * @memberof OmniTransaction
   */
  protected _buildTransaction (
    from: BTCAddress,
    to: BTCAddress,
    value: number,
    propertyId: number,
    unspents: IBTCUnspent[],
    fee: number = 0
  ) {
    this._logger.debug('BTC - OmniTransaction._buildTransaction')

    // 检查可用 value 是否注意完成交易
    const availableValue = sumBy(unspents, 'value')
    const requiredValue = 546 + fee
    if (requiredValue > availableValue) {
      this._logger.error('BTC - OmniTransaction._buildTransaction insufficient balance.')
      if (requiredValue - fee <= availableValue) {
        throw OneChainError.fromCode(109)
      }
      else {
        throw OneChainError.fromCode(102)
      }
    }

    // 构造 transaction
    const builder = new TransactionBuilder(this._network)
    const signList = []

    // 添加 input
    let totalValue = 0
    this._inputs = []
    for (let index = 0; index < unspents.length; index++) {
      if (totalValue > fee + this._minimalValue) {
        break
      }

      const input = unspents[index]
      totalValue += input.value
      builder.addInput(input.txId, input.vout)
      this._inputs.push(input)

      if (this._wallets) {
        const ecpair = this._wallets[input.address]
        signList.push({ index, ecpair })
      }
    }

    if (totalValue < fee + this._minimalValue) {
      throw OneChainError.fromCode(109)
    }
    this._change = totalValue - fee - this._minimalValue

    // 添加 output
    const omniOutput = this._createSimpleSend(value, propertyId)
    builder.addOutput(to, this._minimalValue)
    builder.addOutput(omniOutput, 0)
    this._outputs = [
      { address: to, value: this._minimalValue },
      null,
    ]
    if (this._change > 0) {
      builder.addOutput(from, this._change) // Change
      this._outputs.push({ address: from, value: this._change })
    }

    if (this._wallets) {
      // 签名必须放到添加 output 之后
      signList.forEach(v => {
        builder.sign(v.index, v.ecpair)
      })

      return builder.build()
    }
    else {
      return builder.buildIncomplete()
    }
  }

  protected _validateAddress (address: BTCAddress) {
    if (!isValidAddress(address)) {
      throw OneChainError.fromCode(23)
    }
  }

  /**
   * 验证手续费费率
   *
   * @protected
   * @param {*} feeRate
   * @memberof OmniTransaction
   */
  protected _validateFeeRate (feeRate) {
    if (feeRate <= 0) {
      throw OneChainError.fromCode(105)
    }
  }

  /**
   * 验证转账金额
   *
   * @protected
   * @param {number} value
   * @memberof OmniTransaction
   */
  protected _validateValue (value: number) {
    if (!isNumber(value) || value <= 0) {
      throw OneChainError.fromCode(24)
    }
  }

  /**
   * 创建 omni 的 simple send 输出
   *
   * @protected
   * @param {*} value
   * @param {number} [propertyId=31]
   * @returns
   * @memberof OmniTransaction
   */
  protected _createSimpleSend (value, propertyId = 31) {
    this._type = 0
    this._typeName = 'Simple Send'

    const simpleSend = [
      '6f6d6e69',    // omni
      '0000',        // tx type
      '0000',        // version
      // 0000001f: 31 for Tether
      this._toPaddedHexString(propertyId, 8),
      // 000000003B9ACA00: 10 * 100 000 000 in HEX
      this._toPaddedHexString(value, 16),
    ].join('')

    return script.compile([opcodes.OP_RETURN, Buffer.from(simpleSend, 'hex')])
  }

  /**
   * 数字转为特殊的 16 进制字符串
   *
   * @protected
   * @param {number} num
   * @param {number} len
   * @returns
   * @memberof OmniTransaction
   */
  protected _toPaddedHexString (num: number, len: number) {
    const hex = num.toString(16)
    return '0'.repeat(len - hex.length) + hex
  }
}
